import React from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigationHistory } from './NavigationHistoryProvider';
import { mergeNavigationState, useHistoryPop } from './origin';

export interface HistoryOverlayUpdateOptions {
  canonical?: boolean;
  state?: unknown;
}

export interface HistoryOverlayOptions<T extends string | number> {
  routeId: string;
  queryKey: string;
  value: T | null;
  commitValue: (
    value: T | null,
    options?: HistoryOverlayUpdateOptions,
  ) => void;
  replaceValue: (
    value: T | null,
    options?: HistoryOverlayUpdateOptions,
  ) => void;
}

interface OverlayOwner {
  routeId: string;
  queryKey: string;
  baseKey: string;
}

const OVERLAY_OWNER_KEYS = new Set<PropertyKey>([
  'routeId',
  'queryKey',
  'baseKey',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value) as unknown;
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function ownDataValue(record: object, key: PropertyKey): unknown {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(record, key);
    return descriptor && 'value' in descriptor ? descriptor.value : undefined;
  } catch {
    return undefined;
  }
}

function readOverlayOwner(state: unknown): OverlayOwner | null {
  if (!isRecord(state)) return null;
  const navigation = ownDataValue(state, '__aaNavigation');
  if (!isRecord(navigation)) return null;
  const overlay = ownDataValue(navigation, 'overlay');
  if (!isRecord(overlay)) return null;

  let keys: PropertyKey[];
  try {
    keys = Reflect.ownKeys(overlay);
  } catch {
    return null;
  }
  if (keys.length !== OVERLAY_OWNER_KEYS.size
    || keys.some((key) => !OVERLAY_OWNER_KEYS.has(key))) return null;

  const routeId = ownDataValue(overlay, 'routeId');
  const queryKey = ownDataValue(overlay, 'queryKey');
  const baseKey = ownDataValue(overlay, 'baseKey');
  return typeof routeId === 'string'
    && typeof queryKey === 'string'
    && typeof baseKey === 'string'
    ? { routeId, queryKey, baseKey }
    : null;
}

function removeOverlayOwner(state: unknown, queryKey: string): unknown {
  if (!isRecord(state)) return state;
  const navigation = ownDataValue(state, '__aaNavigation');
  if (!isRecord(navigation)) return state;
  const overlay = ownDataValue(navigation, 'overlay');
  if (!isRecord(overlay) || ownDataValue(overlay, 'queryKey') !== queryKey) return state;

  try {
    const navigationDescriptors = Object.getOwnPropertyDescriptors(navigation);
    delete navigationDescriptors.overlay;
    const nextNavigation = Object.create(
      Object.getPrototypeOf(navigation) as object | null,
      navigationDescriptors,
    ) as Record<string, unknown>;
    const stateDescriptors = Object.getOwnPropertyDescriptors(state);
    if (Reflect.ownKeys(nextNavigation).length === 0) {
      delete stateDescriptors.__aaNavigation;
    } else {
      const navigationDescriptor = stateDescriptors.__aaNavigation;
      stateDescriptors.__aaNavigation = navigationDescriptor && 'value' in navigationDescriptor
        ? { ...navigationDescriptor, value: nextNavigation }
        : {
          configurable: true,
          enumerable: true,
          value: nextNavigation,
          writable: true,
        };
    }
    return Object.create(
      Object.getPrototypeOf(state) as object | null,
      stateDescriptors,
    ) as Record<string, unknown>;
  } catch {
    return state;
  }
}

export function useHistoryOverlay<T extends string | number>(
  options: HistoryOverlayOptions<T>,
) {
  const location = useLocation();
  const history = useNavigationHistory();
  const popOneHistoryEntry = useHistoryPop();
  const selectedId = options.value;

  const open = React.useCallback((id: T) => {
    if (id === selectedId) return;
    options.commitValue(id, {
      state: mergeNavigationState(location.state, {
        overlay: {
          routeId: options.routeId,
          queryKey: options.queryKey,
          baseKey: location.key,
        },
      }),
    });
  }, [location.key, location.state, options, selectedId]);

  const replaceClosed = React.useCallback((canonical: boolean) => {
    options.replaceValue(null, {
      canonical,
      state: removeOverlayOwner(location.state, options.queryKey),
    });
  }, [location.state, options]);
  const removeMissing = React.useCallback(() => {
    replaceClosed(true);
  }, [replaceClosed]);
  const replaceClose = React.useCallback(() => {
    replaceClosed(false);
  }, [replaceClosed]);

  const close = React.useCallback(() => {
    const owner = readOverlayOwner(location.state);
    const predecessor = history.entries[history.cursor - 1];
    if (selectedId !== null
      && owner?.routeId === options.routeId
      && owner.queryKey === options.queryKey
      && history.canGoBack
      && predecessor?.key === owner.baseKey) {
      popOneHistoryEntry();
      return;
    }
    replaceClose();
  }, [
    history.canGoBack,
    history.cursor,
    history.entries,
    location.state,
    options.queryKey,
    options.routeId,
    popOneHistoryEntry,
    replaceClose,
    selectedId,
  ]);

  return React.useMemo(() => ({
    selectedId,
    isOpen: selectedId !== null,
    open,
    close,
    replaceClose,
    removeMissing,
  }), [close, open, removeMissing, replaceClose, selectedId]);
}
